import { useTranslation } from 'react-i18next'

import { ArrowUDownLeftIcon } from '@phosphor-icons/react'

import {
  Card,
  lineItemForReviewReasons,
  lineItemRejectReasons,
  OnboardingModal,
  Pagination,
} from '@reveni-io/reveni-ui'

import CreateViewDrawer from '@/common/components/FilterViews/CreateViewDrawer/CreateViewDrawer'
import FilterViews from '@/common/components/FilterViews/FilterViews'
import ManageVisibilityDrawer from '@/common/components/FilterViews/ManageVisibilityDrawer/ManageVisibilityDrawer'
import PageSizeSelector from '@/common/components/PageSizeSelector/PageSizeSelector'
import TitlePage from '@/common/components/TitlePage/TitlePage'
import { subscriptionPlans } from '@/common/constants'
import CancelOrderConfirmationModal from '@/returns/components/CancelOrderConfirmationModal/CancelOrderConfirmationModal'
import IssueExchangeAndRefundConfirmationModal from '@/returns/components/IssueExchangeAndRefundConfirmationModal/IssueExchangeAndRefundConfirmationModal'
import IssueExchangeConfirmationModal from '@/returns/components/IssueExchangeConfirmationModal/IssueExchangeConfirmationModal'
import IssueRefundConfirmationModal from '@/returns/components/IssueRefundConfirmationModal/IssueRefundConfirmationModal'
import routes from '@/Router/routes.constants'

import AcceptMultipleRefundsModal from './AcceptMultipleRefundsModal/AcceptMultipleRefundsModal'
import BulkOnHoldTransactionsModal from './BulkOnHoldTransactionsModal/BulkOnHoldTransactionsModal'
import CheckAllTransactions from './CheckAllTransactions/CheckAllTransactions'
import ExportModal from './ExportModal/ExportModal'
import Filters from './Filters/Filters'
import useTransactionsPage from './hooks/useTransactionsPage/useTransactionsPage'
import ImportCreateRmasDrawer from './ImportCreateRmasDrawer/ImportCreateRmasDrawer'
import ImportInProgressModal from './ImportInProgressModal/ImportInProgressModal'
import ImportProductValidationDrawer from './ImportProductValidationDrawer/ImportProductValidationDrawer'
import NewRMADrawer from './NewRMADrawer/NewRMADrawer'
import RejectMultipleRefundsModal from './RejectMultipleRefundsModal/RejectMultipleRefundsModal'
import TransactionsList from './TransactionsList/TransactionsList'
import TransactionsMetrics from './TransactionsMetrics/TransactionsMetrics'
import { defaultViews, filterNames } from './constants'
import * as Styles from './styles'

const TransactionsPage = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const {
    data,
    loading,
    info,
    metrics,
    viewState,
    createViewDrawer,
    manageVisibilityDrawer,
    filterState,
    referenceData,
    selection,
    exportState,
    changeStatus,
    newRmaDrawer,
    bulkOnHold,
    importProductValidation,
    importCreateRmas,
    onboarding,
    getReturnLink,
    handleClickInStat,
  } = useTransactionsPage({ isUnifiedDashboard })

  return (
    <Styles.MainContainer>
      <TitlePage Icon={ArrowUDownLeftIcon}>
        {t('Transacciones')} - {isUnifiedDashboard ? 'Global' : info?.selectedStore?.name}
      </TitlePage>
      <TransactionsMetrics
        metrics={metrics}
        subscriptionPlan={info?.subscription_plan || subscriptionPlans.premium}
        onClickInStats={handleClickInStat}
      />
      <FilterViews
        loading={viewState.fetchingViews}
        customViews={viewState.views}
        selectedView={viewState.selectedView}
        onChangeView={viewState.handleChangeView}
        onDuplicateView={createViewDrawer.openDuplicateViewDrawer}
        onDeleteView={viewState.deleteView}
        hasViewChanges={viewState.hasViewChanges}
        onSaveChanges={viewState.handleSaveView}
        onCreateView={createViewDrawer.openCreateViewDrawer}
        onManageVisibility={manageVisibilityDrawer.openManageVisibilityDrawer}
        onDiscardChanges={viewState.handleDiscardViewChanges}
        onExportResults={() => exportState.setShowExportModal(true)}
        defaultViews={defaultViews}
        canEditViews={viewState.canEditViews}
        canManageSelectedView={viewState.canManageSelectedView}
        canViewDefaultViews={viewState.canViewDefaultViews}
        hasAvailableViews={viewState.hasAvailableViews}
        showVisibilityIndicator
        onImportProductValidation={
          isUnifiedDashboard ? undefined : importProductValidation.openImportProductValidationDrawer
        }
        onImportCreateRmas={isUnifiedDashboard ? undefined : importCreateRmas.openImportCreateRmasDrawer}
      />
      <Styles.ListCardContainer>
        <Card showBorder={false}>
          <Filters
            loading={viewState.fetchingViews}
            onChangeFilter={filterState.onChangeFilter}
            filters={filterState.filterProps}
            query={filterState.query}
            onApplyFilters={filterState.refetch}
            orderBy={filterState.orderBy}
            subscriptionPlan={info?.subscription_plan || subscriptionPlans.basic}
            returnReasons={referenceData.returnReasons || []}
            rejectReasons={lineItemRejectReasons || []}
            forReviewReasons={lineItemForReviewReasons || []}
            regions={referenceData.regions || []}
            onOpenNewRMADrawer={newRmaDrawer.openNewRMADrawer}
            productCategories={referenceData.productCategories}
            productCollections={referenceData.productCollections}
            productTypes={referenceData.productTypes}
            isUnifiedDashboard={isUnifiedDashboard}
            carriers={referenceData.carriers}
            deliveryMethods={referenceData.usedDeliveryMethods}
            availableAlertTypes={referenceData.availableAlertTypes}
            fetchingAlertTypes={referenceData.fetchingAlertTypes}
            policyRules={referenceData.policyRules}
            readOnly={!viewState.canEditViews}
            disabled={!viewState.hasAvailableViews}
          />
          <Styles.OrdersContainer>
            {!isUnifiedDashboard && viewState.hasAvailableViews && (
              <CheckAllTransactions
                selectedOrders={selection.selectedOrders}
                orders={data?.results}
                onCheckAllOrder={selection.onCheckAllOrder}
                onAcceptSelected={() => selection.setShowAcceptMultipleRefundModal(true)}
                onRejectSelected={() => selection.setShowRejectMultipleRefundModal(true)}
                onApproveSelected={bulkOnHold.onApproveOnHoldTransactions}
                onDeclineSelected={bulkOnHold.onDeclineOnHoldTransactions}
              />
            )}
            <TransactionsList
              orders={data?.results}
              loading={
                loading ||
                viewState.fetchingViews ||
                referenceData.fetchingReturnReasons ||
                referenceData.fetchingRegions
              }
              getReturnLink={getReturnLink}
              onChangeStatus={changeStatus.onChangeStatus}
              selectedOrders={selection.selectedOrders}
              onCheckOrder={selection.onCheckOrder}
              showCheckBox={!isUnifiedDashboard}
              isUnifiedDashboard={isUnifiedDashboard}
              getStoreNameById={referenceData.getStoreNameById}
              hasAvailableViews={viewState.hasAvailableViews}
            />
          </Styles.OrdersContainer>
          <Styles.PaginationContainer>
            <PageSizeSelector
              value={filterState.returnsPageSize}
              onChange={pageSize => filterState.onChangeFilter(filterNames.pageSize, pageSize)}
            />
            <Pagination
              next={data?.next}
              previous={data?.previous}
              onChangePage={(page, cursor) => filterState.onChangeFilter(filterNames.returnsCursor, cursor)}
              isCursorPagination
            />
          </Styles.PaginationContainer>
          <ExportModal
            show={exportState.showExportModal}
            onClose={() => exportState.setShowExportModal(false)}
            onExport={exportState.onExportResults}
            exporting={exportState.exportingResults}
          />
          <AcceptMultipleRefundsModal
            show={selection.showAcceptMultipleRefundModal}
            notUsableOrders={selection.notUsableOrders}
            selectedOrders={selection.selectedOrders}
            ordersWithRequestedProducts={selection.getOrdersWithRequestedProducts()}
            ordersWithRemainingAmount={selection.getOrdersWithRemainingAmount()}
            onAccept={selection.onChangeMultipleRefunds}
            onClose={() => selection.setShowAcceptMultipleRefundModal(false)}
            allowIssueRefund={selection.getSelectedOrdersAllowIssueRefund()}
            allowManageRestock={selection.getSelectedOrdersAllowManageRestock()}
            allowsExchangeOrderProcessing={selection.getSelectedOrdersAllowsExchangeOrderProcessing()}
            currency={info?.selectedStore?.currency}
            accepting={selection.changingMultipleReturnStatus}
            issueRefundAtStatus={selection.getSelectedOrdersIssueRefundAtStatus()}
          />
          <RejectMultipleRefundsModal
            show={selection.showRejectMultipleRefundModal}
            notUsableOrders={selection.notUsableOrders}
            selectedOrders={selection.selectedOrders}
            rejectReasons={lineItemRejectReasons}
            onReject={selection.onChangeMultipleRefunds}
            onClose={() => selection.setShowRejectMultipleRefundModal(false)}
            rejecting={selection.changingMultipleReturnStatus}
            ordersWithRequestedProducts={selection.getOrdersWithRequestedProducts()}
            allowsExchangeOrderProcessing={selection.getSelectedOrdersAllowsExchangeOrderProcessing()}
            allowsUpsells={info?.selectedStore?.preferences?.allows_upsells}
          />
          <IssueRefundConfirmationModal
            show={changeStatus.showIssueRefundConfirmationModal}
            onClose={() => changeStatus.setShowIssueRefundConfirmationModal(false)}
            onConfirm={changeStatus.handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived}
          />
          <IssueExchangeConfirmationModal
            show={changeStatus.showIssueExchangeConfirmationModal}
            onClose={() => changeStatus.setShowIssueExchangeConfirmationModal(false)}
            onConfirm={changeStatus.handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived}
          />
          <IssueExchangeAndRefundConfirmationModal
            show={changeStatus.showIssueExchangeAndRefundConfirmationModal}
            onClose={() => changeStatus.setShowIssueExchangeAndRefundConfirmationModal(false)}
            onConfirm={changeStatus.handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived}
          />
          <CancelOrderConfirmationModal
            show={changeStatus.showCancelOrderConfirmationModal}
            onClose={() => changeStatus.setShowCancelOrderConfirmationModal(false)}
            onConfirm={changeStatus.handleConfirmCancelOrder}
          />
        </Card>
      </Styles.ListCardContainer>
      <OnboardingModal
        show={onboarding.showCompleteConfigurationModal}
        onClose={onboarding.onCloseConfigurationModal}
        logisticCompleted={info?.has_delivery_methods}
        brandingCompleted={info?.selectedReturnPortal?.theme?.logo_url}
        returnsPortalUrl={info?.selectedReturnPortal?.returns_portal_url}
        logisticUrl={routes.configurationPage.logistics.deliveryMethods}
        brandingUrl={routes.configurationPage.branding}
        storeUrl={routes.configurationPage.store}
      />
      <CreateViewDrawer
        show={createViewDrawer.showCreateViewDrawer}
        onClose={createViewDrawer.closeCreateViewDrawer}
        onSave={createViewDrawer.handleCreateView}
        saving={viewState.creatingView}
        initialName={createViewDrawer.initialViewName}
        groups={createViewDrawer.groups}
        fixedGroupIds={createViewDrawer.fixedGroupIds}
        canSelectGroups={viewState.canShareViewsWithAllGroups}
        loadingGroups={createViewDrawer.fetchingUserGroups}
      />
      <ManageVisibilityDrawer
        show={manageVisibilityDrawer.showManageVisibilityDrawer}
        onClose={manageVisibilityDrawer.closeManageVisibilityDrawer}
        onSave={manageVisibilityDrawer.handleSaveVisibility}
        saving={viewState.savingView}
        view={manageVisibilityDrawer.view}
        groups={manageVisibilityDrawer.groups}
        ownGroupIds={manageVisibilityDrawer.ownGroupIds}
        canSelectGroups={viewState.canShareViewsWithAllGroups}
        loadingGroups={manageVisibilityDrawer.fetchingUserGroups}
      />
      <NewRMADrawer
        show={newRmaDrawer.showNewRMADrawer}
        onClose={newRmaDrawer.closeNewRMADrawer}
        onChangeSearchValue={newRmaDrawer.setSearchValue}
        searchValue={newRmaDrawer.searchValue}
        onSearchOrders={newRmaDrawer.onSearchOrders}
        orders={newRmaDrawer.orders}
        searchingOrders={newRmaDrawer.searchingOrders}
        onViewOrder={newRmaDrawer.onViewOrder}
      />
      <BulkOnHoldTransactionsModal
        show={bulkOnHold.showBulkOnHoldTransactionsModal}
        onClose={bulkOnHold.closeBulkOnHoldTransactionsModal}
        mode={bulkOnHold.bulkOnHoldTransactionsModalMode}
        approving={bulkOnHold.approvingBulkOnHoldTransactions}
        declining={bulkOnHold.decliningBulkOnHoldTransactions}
        onApprove={bulkOnHold.approveOnHoldTransactions}
        onDecline={bulkOnHold.declineOnHoldTransactions}
        selectedOrders={selection.selectedOrders}
      />
      <ImportProductValidationDrawer
        show={importProductValidation.showImportProductValidationDrawer}
        onClose={importProductValidation.closeImportProductValidationDrawer}
        onSelectValidationFile={importProductValidation.onSelectValidationFile}
        onDeleteValidationFile={importProductValidation.onDeleteValidationFile}
        validationFile={importProductValidation.validationFile}
        validationError={importProductValidation.validationError}
        onError={importProductValidation.setValidationError}
        onImport={importProductValidation.onImportValidationFile}
        importing={importProductValidation.importing}
      />

      <ImportCreateRmasDrawer
        show={importCreateRmas.showImportCreateRmasDrawer}
        onClose={importCreateRmas.closeImportCreateRmasDrawer}
        onSelectFile={importCreateRmas.onSelectImportFile}
        onDeleteFile={importCreateRmas.onDeleteImportFile}
        file={importCreateRmas.importFile}
        error={importCreateRmas.importError}
        onError={importCreateRmas.setImportError}
        onImport={importCreateRmas.onImportCreateRmasFile}
        importing={importCreateRmas.importing}
      />
      <ImportInProgressModal
        show={importProductValidation.showImportInProgressModal || importCreateRmas.showImportInProgressModal}
        onClose={() => {
          if (importProductValidation.showImportInProgressModal) importProductValidation.onCloseImportInProgressModal()
          if (importCreateRmas.showImportInProgressModal) importCreateRmas.onCloseImportInProgressModal()
        }}
        userEmail={info?.userInfo?.email}
      />
    </Styles.MainContainer>
  )
}

export default TransactionsPage
